import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ActionButton } from "../../components/Buttons";
import Footer from "../../components/Footer";
import { auth } from "../../configs/firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";
import {
  getPreferredAuthUser,
  waitForSettledAuthUser,
} from "../../utils/auth/authState";
import { trackEvent, trackMixpanelEvent } from "../../utils/Analytics";
import {
  PENDING_AUTH_INTENT_ACTIONS,
  savePendingAuthIntent,
} from "../../utils/auth/pendingAuthIntent";
import {
  hasAnonymousGenerationBeenUsed,
  markAnonymousGenerationUsed,
} from "../../utils/auth/anonymousGenerationUsage";
import { refineEmpathyCard } from "../../utils/empathyAI/refineEmpathyCard";
import {
  getFriendlyRefineErrorMessage,
  getSafeRefineDiagnostic,
} from "../../utils/empathyAI/refineError";
import {
  getRefineContextFromSearch,
  getRefineContextFromWall,
  getWallIdFromSearch,
} from "../../utils/empathyAI/refineContexts";
import { getPublicActiveWallById } from "../../utils/firestore";
import { getValidShareId } from "../../utils/cards/shareAttribution";
import { ensureAnonymousAuth } from "../../utils/auth/anonymousAuth";
import AccountLinkingSheet from "./components/AccountLinkingSheet";
import {
  getAccountMenuIntent,
  getAccountSessionRecoveryState,
} from "../../utils/auth/accountSessionRecovery";
import BackgroundLights from "./components/BackgroundLights";
import CreatingCardScreen from "./components/CreatingCardScreen";
import EmpathyPageHeader from "./components/EmpathyPageHeader";
import RefineStartingExamples from "./components/RefineStartingExamples";
import { normalizeImmediateGenerationResult } from "./refineResultState";

const REFINE_DARK_BACKGROUND = "#050604";

const TRANSLATION_LANGUAGE_OPTIONS = [
  { value: "", label: "No translation" },
  { value: "English", label: "English" },
  { value: "Arabic", label: "Arabic" },
  { value: "Simplified Chinese", label: "Chinese (Simplified)" },
  { value: "French", label: "French" },
  { value: "Haitian Creole", label: "Haitian Creole" },
  { value: "Hindi", label: "Hindi" },
  { value: "Icelandic", label: "Icelandic" },
  { value: "Japanese", label: "Japanese" },
  { value: "Korean", label: "Korean" },
  { value: "Persian", label: "Persian (Farsi)" },
  { value: "Portuguese", label: "Portuguese" },
  { value: "Russian", label: "Russian" },
  { value: "Spanish", label: "Spanish" },
  { value: "Tagalog", label: "Tagalog" },
  { value: "Vietnamese", label: "Vietnamese" },
];

const RAW_THOUGHT_MAX_LENGTH = 200;
const MIN_CREATE_ANIMATION_MS = 1600;

function formatRefineError(error) {
  return getFriendlyRefineErrorMessage(error);
}

function isAnonymousGenerationLimitError(error) {
  return (
    error?.code === "account-required" &&
    error?.reason === "anonymous-generation-limit"
  );
}

function getSafeRefineReturnPath({
  partnerId,
  wallId,
  enteredWithWallId,
  sourceShareId,
}) {
  const params = new URLSearchParams();

  if (enteredWithWallId && wallId) {
    params.set("wallId", wallId);
  } else if (partnerId === "mcaad") {
    params.set("partner", partnerId);
  }

  if (sourceShareId) {
    params.set("shareId", sourceShareId);
  }

  const query = params.toString();
  return query ? `/refine?${query}` : "/refine";
}

function waitForMinimumCreateAnimation(startedAt) {
  const elapsed = Date.now() - startedAt;
  const remainingAnimationTime = MIN_CREATE_ANIMATION_MS - elapsed;

  if (remainingAnimationTime <= 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) =>
    setTimeout(resolve, remainingAnimationTime)
  );
}

function RefineRouteState({ title, message }) {
  return (
    <div className="relative mx-auto max-w-lg">
      <div className="relative z-10 rounded-[1.35rem] border border-[#f4d94e]/24 bg-white/[0.07] px-5 py-6 text-center shadow-[0_18px_54px_rgba(0,0,0,0.24)] backdrop-blur-md">
        <h2 className="font-serif text-2xl font-medium leading-tight text-white sm:text-3xl">
          {title}
        </h2>

        <p className="mx-auto mt-3 max-w-sm text-sm font-semibold leading-6 text-white/68">
          {message}
        </p>
      </div>
    </div>
  );
}

export default function RefineView({
  forceLoadingScreen = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const rawThoughtInputRef = useRef(null);
  const cardCreationStartedRef = useRef(false);
  const createCardsInFlightRef = useRef(false);

  const requestedWallId = getWallIdFromSearch(searchParams);
  const sourceShareId = getValidShareId(searchParams.get("shareId"));
  const legacyRefineContext = getRefineContextFromSearch(searchParams);
  const [wallRefineContext, setWallRefineContext] = useState(null);
  const [wallContextLoadState, setWallContextLoadState] = useState(
    requestedWallId ? "loading" : "idle"
  );
  const refineContext = requestedWallId
    ? wallRefineContext || legacyRefineContext
    : legacyRefineContext;

  const {
    partnerId,
    wallId,
    analyticsLabel,
  } = refineContext;

  const [rawThought, setRawThought] = useState("");
  const [translationLanguage, setTranslationLanguage] =
    useState("");
  const [loading, setLoading] = useState(false);
  const [refineError, setRefineError] = useState("");
  const [isAccountLinkingOpen, setIsAccountLinkingOpen] =
    useState(false);
  const [
    hasUsedAnonymousFreeGeneration,
    setHasUsedAnonymousFreeGeneration,
  ] = useState(false);
  const restoredAccountDraftRef = useRef(false);

  useEffect(() => {
    if (restoredAccountDraftRef.current) {
      return;
    }

    const currentPath = `${location.pathname}${location.search}`;
    const savedIntent = getAccountMenuIntent();
    const recoveryState = getAccountSessionRecoveryState();
    const savedState = recoveryState || savedIntent;

    if (savedState?.returnTo !== currentPath || !savedState?.draft) {
      return;
    }

    restoredAccountDraftRef.current = true;

    if (typeof savedState.draft.rawThought === "string") {
      setRawThought(savedState.draft.rawThought);
    }

    if (typeof savedState.draft.translationLanguage === "string") {
      setTranslationLanguage(savedState.draft.translationLanguage);
    }
  }, [location.pathname, location.search]);

  const activeUser = getPreferredAuthUser(auth.currentUser, currentUser);
  const analyticsEntryPoint = sourceShareId
    ? "friend_share"
    : partnerId || wallId
      ? "partner_wall"
      : "direct";
  const analyticsUserState = activeUser?.isAnonymous === true
    ? "anonymous"
    : "signed_in";
  const wallContextUnavailable =
    requestedWallId && wallContextLoadState === "unavailable";
  const wallContextLoading =
    requestedWallId && wallContextLoadState === "loading";
  const enteredWithWallId = Boolean(requestedWallId);
  const refineReturnPath = getSafeRefineReturnPath({
    partnerId,
    wallId,
    enteredWithWallId,
    sourceShareId,
  });

  const updateRawThought = (nextRawThought) => {
    if (!cardCreationStartedRef.current && nextRawThought.trim()) {
      cardCreationStartedRef.current = true;
      trackMixpanelEvent("Card Creation Started", {
        entry_point: analyticsEntryPoint,
        user_state: analyticsUserState,
        language: translationLanguage || navigator.language,
        card_type: "generated",
      });
    }

    setRawThought(
      nextRawThought.slice(0, RAW_THOUGHT_MAX_LENGTH)
    );
    setRefineError("");
  };

  const updateTranslationLanguage = (
    nextTranslationLanguage
  ) => {
    setTranslationLanguage(nextTranslationLanguage);
    setRefineError("");
  };

  const handleExampleSelected = (exampleText) => {
    updateRawThought(exampleText);
    rawThoughtInputRef.current?.focus();
  };

  useEffect(() => {
    let isMounted = true;

    if (!requestedWallId) {
      setWallRefineContext(null);
      setWallContextLoadState("idle");
      return () => {
        isMounted = false;
      };
    }

    setWallRefineContext(null);
    setWallContextLoadState("loading");

    const loadWallContext = async () => {
      try {
        const wall = await getPublicActiveWallById(requestedWallId);

        if (!isMounted) {
          return;
        }

        const nextContext = getRefineContextFromWall(wall);

        if (!nextContext) {
          setWallContextLoadState("unavailable");
          return;
        }

        setWallRefineContext(nextContext);
        setWallContextLoadState("loaded");
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error(
          "Unable to load refine wall context",
          getSafeRefineDiagnostic(error, "wall-context")
        );
        setWallContextLoadState("unavailable");
      }
    };

    loadWallContext();

    return () => {
      isMounted = false;
    };
  }, [requestedWallId]);

  useEffect(() => {
    const nextActiveUser = getPreferredAuthUser(auth.currentUser, currentUser);

    setHasUsedAnonymousFreeGeneration(
      hasAnonymousGenerationBeenUsed(nextActiveUser)
    );
  }, [currentUser]);

  useLayoutEffect(() => {
    const rootElement = document.getElementById("root");

    let themeColorMeta = document.querySelector(
      'meta[name="theme-color"]'
    );

    let statusBarMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]'
    );

    const createdThemeColorMeta = !themeColorMeta;
    const createdStatusBarMeta = !statusBarMeta;

    const previousHtmlBackground =
      document.documentElement.style.backgroundColor;
    const previousBodyBackground =
      document.body.style.backgroundColor;
    const previousRootBackground =
      rootElement?.style.backgroundColor || "";
    const previousThemeColor =
      themeColorMeta?.getAttribute("content") || "";
    const previousStatusBarStyle =
      statusBarMeta?.getAttribute("content") || "";

    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.setAttribute("name", "theme-color");
      document.head.appendChild(themeColorMeta);
    }

    if (!statusBarMeta) {
      statusBarMeta = document.createElement("meta");
      statusBarMeta.setAttribute(
        "name",
        "apple-mobile-web-app-status-bar-style"
      );
      document.head.appendChild(statusBarMeta);
    }

    document.documentElement.style.backgroundColor =
      REFINE_DARK_BACKGROUND;
    document.body.style.backgroundColor =
      REFINE_DARK_BACKGROUND;

    themeColorMeta.setAttribute(
      "content",
      REFINE_DARK_BACKGROUND
    );

    statusBarMeta.setAttribute("content", "black");

    if (rootElement) {
      rootElement.style.backgroundColor =
        REFINE_DARK_BACKGROUND;
    }

    return () => {
      document.documentElement.style.backgroundColor =
        previousHtmlBackground;

      document.body.style.backgroundColor =
        previousBodyBackground;

      if (rootElement) {
        rootElement.style.backgroundColor =
          previousRootBackground;
      }

      if (createdThemeColorMeta) {
        themeColorMeta.remove();
      } else {
        themeColorMeta.setAttribute(
          "content",
          previousThemeColor
        );
      }

      if (createdStatusBarMeta) {
        statusBarMeta.remove();
      } else {
        statusBarMeta.setAttribute(
          "content",
          previousStatusBarStyle
        );
      }
    };
  }, []);

  const canSubmit = useMemo(
    () =>
      rawThought.trim().length > 8 &&
      !loading &&
      !wallContextLoading &&
      !wallContextUnavailable,
    [rawThought, loading, wallContextLoading, wallContextUnavailable]
  );

  const createCards = async ({
    nextRawThought,
    nextTranslationLanguage,
    nextSourceShareId = sourceShareId,
    allowAccountLinking,
    expectedOwnerUid = "",
  }) => {
    if (wallContextLoading) {
      setRefineError("Opening this wall. Please try again in a moment.");
      return;
    }

    if (wallContextUnavailable) {
      setRefineError("This wall isn't available right now.");
      return;
    }

    if (createCardsInFlightRef.current) {
      return;
    }

    createCardsInFlightRef.current = true;

    const animationStartedAt = Date.now();

    setRefineError("");
    setLoading(true);

    trackEvent(
      "EmpathyAI",
      "Create card started",
      analyticsLabel
    );

    try {
      const refineRequest = {
        rawThought: nextRawThought,
        translationLanguage: nextTranslationLanguage,
        partnerId,
        wallId,
      };

      if (nextSourceShareId) {
        refineRequest.sourceShareId = nextSourceShareId;
      }

      const hasAuthSource = Boolean(auth.currentUser || currentUser);
      let settledUser = hasAuthSource
        ? await waitForSettledAuthUser({
            contextUser: currentUser,
            expectedUid: expectedOwnerUid,
          })
        : null;

      if (!settledUser && !hasAuthSource && !expectedOwnerUid) {
        const anonymousUser = await ensureAnonymousAuth();
        settledUser = await waitForSettledAuthUser({
          contextUser: anonymousUser,
          expectedUid: anonymousUser?.uid || "",
        });
      }

      if (!settledUser) {
        const authError = new Error(
          "We could not verify your account session. Please try again in a moment."
        );
        authError.code = "auth/user-state-not-settled";
        throw authError;
      }

      trackMixpanelEvent("Card Generation Requested", {
        entry_point: nextSourceShareId
          ? "friend_share"
          : partnerId || wallId
            ? "partner_wall"
            : "direct",
        user_state: analyticsUserState,
        language: nextTranslationLanguage || navigator.language,
        card_type: "generated",
      });

      const refined = await refineEmpathyCard(refineRequest, {
        user: settledUser,
        expectedUid: expectedOwnerUid,
      });

      if (!refined.generationId) {
        throw new Error(
          "Create cards response did not include a generation ID"
        );
      }

      await waitForMinimumCreateAnimation(
        animationStartedAt
      );

      trackEvent(
        "EmpathyAI",
        "Create card completed",
        analyticsLabel
      );

      if (
        markAnonymousGenerationUsed(settledUser)
      ) {
        setHasUsedAnonymousFreeGeneration(true);
      }

      navigate(`/result/${encodeURIComponent(refined.generationId)}`, {
        state: {
          fromLettuceTalk: true,
          immediateResult: normalizeImmediateGenerationResult(
            refined,
            nextTranslationLanguage
          ),
          ...(expectedOwnerUid ? { expectedOwnerUid } : {}),
        },
      });
    } catch (error) {
      console.error(
        "Unable to refine card",
        getSafeRefineDiagnostic(error, "generation")
      );

      trackEvent(
        "EmpathyAI",
        "Create card failed",
        analyticsLabel
      );

      await waitForMinimumCreateAnimation(
        animationStartedAt
      );

      if (
        allowAccountLinking &&
        isAnonymousGenerationLimitError(error)
      ) {
        savePendingAuthIntent({
          action:
            PENDING_AUTH_INTENT_ACTIONS.CREATE_ANOTHER,
          returnTo: refineReturnPath,
          partner: partnerId,
          sourceShareId: nextSourceShareId,
          draft: {
            rawThought: nextRawThought,
            translationLanguage:
              nextTranslationLanguage,
          },
        });

        setRefineError("");
        setIsAccountLinkingOpen(true);
        setLoading(false);
        return;
      }

      setRefineError(formatRefineError(error));
      setLoading(false);
    } finally {
      createCardsInFlightRef.current = false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    await createCards({
      nextRawThought: rawThought,
      nextTranslationLanguage: translationLanguage,
      allowAccountLinking: true,
    });
  };

  const openCreateMoreAccountPrompt = () => {
    savePendingAuthIntent({
      action: PENDING_AUTH_INTENT_ACTIONS.CREATE_ANOTHER,
      returnTo: refineReturnPath,
      partner: partnerId,
      sourceShareId,
      draft: {
        rawThought,
        translationLanguage,
      },
    });
    setRefineError("");
    setIsAccountLinkingOpen(true);
  };

  const continueAfterAccountLinking = async ({
    authResult,
    pendingIntent,
  } = {}) => {
    const pendingDraft = pendingIntent?.draft || {};

    const nextRawThought =
      typeof pendingDraft.rawThought === "string"
        ? pendingDraft.rawThought
        : rawThought;

    const nextTranslationLanguage =
      typeof pendingDraft.translationLanguage ===
      "string"
        ? pendingDraft.translationLanguage
        : translationLanguage;
    const nextSourceShareId = getValidShareId(
      pendingIntent?.sourceShareId || sourceShareId
    );
    const expectedOwnerUid =
      typeof authResult?.uid === "string" ? authResult.uid.trim() : "";

    setIsAccountLinkingOpen(false);
    setRawThought(nextRawThought);
    setTranslationLanguage(nextTranslationLanguage);
    setHasUsedAnonymousFreeGeneration(false);

    if (!nextRawThought.trim()) {
      return;
    }

    await createCards({
      nextRawThought,
      nextTranslationLanguage,
      nextSourceShareId,
      allowAccountLinking: false,
      expectedOwnerUid,
    });
  };

  const showLoadingScreen =
    loading || forceLoadingScreen;

  const showInlineCreateMoreAccountState =
    activeUser?.isAnonymous === true &&
    hasUsedAnonymousFreeGeneration &&
    !rawThought.trim() &&
    !isAccountLinkingOpen;

  return (
    <div
      className={
          showLoadingScreen
          ? "flex h-[100dvh] flex-col overflow-hidden overscroll-none font-sans text-white"
          : "flex min-h-screen min-h-[100dvh] flex-col overflow-x-hidden overscroll-y-none font-sans text-white"
      }
      style={{
        backgroundColor: REFINE_DARK_BACKGROUND,
      }}
    >
      <Helmet>
        <meta
          name="theme-color"
          content={REFINE_DARK_BACKGROUND}
        />

        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black"
        />
      </Helmet>

      <style>{`
        @keyframes refine-drift {
          0%,
          100% {
            transform: translate3d(-2%, 0, 0);
            opacity: 0.52;
          }

          50% {
            transform: translate3d(2%, -8px, 0);
            opacity: 0.88;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .refine-ambient-motion {
            animation: none !important;
          }
        }

        .refine-textarea-wrapper,
        .refine-textarea-wrapper * {
          animation: none !important;
          contain: none !important;
          filter: none !important;
          -webkit-backdrop-filter: none !important;
          backdrop-filter: none !important;
          perspective: none !important;
          transform: none !important;
          will-change: auto !important;
        }
      `}</style>

      <main
          className={
            showLoadingScreen
              ? "relative h-full flex-1 overflow-hidden"
              : "relative flex-1"
          }
      >
        <div
          className={
            showLoadingScreen
              ? "pointer-events-none fixed inset-0 -z-10 bg-[#050604]"
              : "pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_50%_92%,rgba(244,217,78,0.32)_0%,rgba(48,118,9,0.18)_18%,transparent_38%),radial-gradient(circle_at_50%_112%,rgba(255,255,255,0.18)_0%,transparent_38%),linear-gradient(180deg,#020302_0%,#070806_38%,#111309_74%,#050604_100%)]"
          }
        />

        {!showLoadingScreen && (
          <div
            className="refine-ambient-motion pointer-events-none fixed inset-x-[-18%] top-12 -z-10 h-[52vh] opacity-20"
            style={{
              animation:
                "refine-drift 24s ease-in-out infinite",
            }}
          >
            <svg
              viewBox="0 0 1400 520"
              preserveAspectRatio="none"
              className="h-full w-full"
              fill="none"
            >
              <path
                d="M-70 130 C140 18 258 196 446 104 C614 22 748 66 908 146 C1074 230 1204 128 1470 70"
                stroke="rgba(255,255,255,0.72)"
                strokeWidth="3"
                strokeLinecap="round"
              />

              <path
                d="M-80 254 C92 156 250 276 428 216 C630 148 768 186 930 274 C1110 370 1244 244 1480 196"
                stroke="rgba(255,255,255,0.44)"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}

        <section
          className={
            showLoadingScreen
              ? "relative mx-auto flex h-full max-w-5xl flex-col overflow-hidden px-5 py-4 sm:px-8 sm:py-6"
              : "mx-auto max-w-5xl px-7 pb-28 pt-4 sm:px-10 sm:pt-6"
          }
        >
          {showLoadingScreen ? (
            <>
              <EmpathyPageHeader
                title="LettuceTalk"
                authUser={activeUser}
                accountSessionDraft={{
                  rawThought,
                  translationLanguage,
                }}
                showBackButton={false}
                signOutRedirectTo="/refine"
              />

              <div className="flex min-h-0 flex-1 items-center justify-center">
                <CreatingCardScreen />
              </div>
            </>
          ) : (
            <>
              <EmpathyPageHeader
                title="LettuceTalk"
                authUser={activeUser}
                accountSessionDraft={{
                  rawThought,
                  translationLanguage,
                }}
                showBackButton={false}
                signOutRedirectTo="/refine"
                className="mb-8"
              />

              <header className="mb-6 text-center sm:mb-8">
                <h1 className="mx-auto mt-2 max-w-3xl font-serif text-3xl font-medium leading-[1] tracking-[-0.035em] text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.12)] sm:text-5xl">
                  What do you wish you could ask a
                  loved one?
                </h1>

                <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-gray-300">
                  A thought, feeling, or worry is enough.<br/>We'll shape it into a gentle card you can share.
                </p>
              </header>

              {wallContextUnavailable ? (
                <RefineRouteState
                  title="This wall isn't available."
                  message="It may be private, inactive, or the link may no longer work."
                />
              ) : showInlineCreateMoreAccountState ? (
                <div className="relative mx-auto max-w-lg">
                  <div className="relative z-10 rounded-[1.35rem] border border-[#f4d94e]/24 bg-white/[0.07] px-5 py-6 text-center shadow-[0_18px_54px_rgba(0,0,0,0.24)] backdrop-blur-md">
                    <h2 className="font-serif text-2xl font-medium leading-tight text-white sm:text-3xl">
                      Create a free account to make more cards
                    </h2>

                    <p className="mx-auto mt-3 max-w-sm text-sm font-semibold leading-6 text-white/68">
                      You’ve already made your free card set. Create an account to keep going and come back to your cards later.
                    </p>

                    <ActionButton
                      type="button"
                      onClick={openCreateMoreAccountPrompt}
                      fullWidth
                      icon="sparkles"
                      size="sm"
                      className="mt-5"
                    >
                      Create free account
                    </ActionButton>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="relative mx-auto max-w-lg"
                >
                  <div className="relative z-10 space-y-5">
                  <div>
                    <div className="relative">
                      <BackgroundLights wide />

                      <div className="pointer-events-none absolute inset-x-[-24vw] top-[-18rem] bottom-[-2rem] hidden rounded-[50%] bg-[radial-gradient(ellipse_at_50%_48%,rgba(244,217,78,0.18)_0%,rgba(106,174,37,0.1)_32%,rgba(255,255,255,0.05)_56%,transparent_78%)] blur-3xl sm:block" />

                      <div className="refine-textarea-wrapper relative z-10 mt-3 box-border min-h-[116px] w-full rounded-[1.35rem] border border-[#fff8df]/90 bg-white/95 px-5 pb-10 pt-6 shadow-[0_20px_52px_rgba(0,0,0,0.26),0_0_18px_rgba(255,252,232,0.58),0_0_48px_rgba(244,217,78,0.3),inset_0_1px_0_rgba(255,255,255,0.94)] backdrop-blur-sm transition duration-300 focus-within:border-[#f4d94e]/80 focus-within:ring-4 focus-within:ring-[#f4d94e]/15 focus-within:shadow-[0_20px_52px_rgba(0,0,0,0.26),0_0_24px_rgba(255,252,232,0.72),0_0_64px_rgba(244,217,78,0.4),inset_0_1px_0_rgba(255,255,255,0.96)] sm:min-h-[140px]">
                        <textarea
                          id="rawThought"
                          ref={rawThoughtInputRef}
                          value={rawThought}
                          onChange={(event) =>
                            updateRawThought(
                              event.target.value
                            )
                          }
                          maxLength={
                            RAW_THOUGHT_MAX_LENGTH
                          }
                          rows={4}
                          placeholder="Mom, I need more help to take care of you, but I'm scared to tell you..."
                          dir="auto"
                          className="block h-[4.5rem] w-full resize-none scroll-pb-4 border-0 bg-transparent p-0 font-sans text-base leading-6 text-gray-950 outline-none placeholder:text-gray-400 sm:h-[5.25rem] sm:text-lg sm:leading-7"
                        />

                        <div className="pointer-events-none absolute bottom-4 right-5 z-20">
                          <span className="text-xs font-black text-gray-300">
                            {rawThought.length}/
                            {RAW_THOUGHT_MAX_LENGTH}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <RefineStartingExamples
                    onSelectExample={handleExampleSelected}
                  />

                  <div className="!mt-2 flex h-10 items-center gap-2 rounded-full border border-white/40 px-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                    <label
                      htmlFor="translationLanguage"
                      className="shrink-0 text-[11px] font-medium leading-none tracking-normal text-white/68"
                    >
                      Add Translations
                    </label>

                    <span
                      aria-hidden="true"
                      className="h-5 w-px shrink-0 bg-white/10"
                    />

                    <div className="relative min-w-0 flex-1">
                      <select
                        id="translationLanguage"
                        value={translationLanguage}
                        onChange={(event) =>
                          updateTranslationLanguage(
                            event.target.value
                          )
                        }
                        className="h-8 w-full min-w-0 appearance-none truncate rounded-full border border-transparent bg-transparent py-0 px-2 text-xs font-normal text-white outline-none transition hover:text-white focus:border-white/20 focus:ring-2 focus:ring-white/10 sm:text-sm"
                      >
                        {TRANSLATION_LANGUAGE_OPTIONS.map(
                          (option) => (
                            <option
                              key={
                                option.value || "none"
                              }
                              value={option.value}
                              className="bg-[#111309] text-white"
                            >
                              {option.label}
                            </option>
                          )
                        )}
                      </select>

                      <svg
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.4}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="pointer-events-none absolute right-0 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/78"
                      >
                        <path d="m5 7.5 5 5 5-5" />
                      </svg>
                    </div>
                  </div>

                  <div className="inline-flex items-baseline gap-1.5 text-xs text-white/56 sm:gap-2 sm:text-sm">
                    <svg
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.8}
                      stroke="currentColor"
                      className="relative top-[2px] h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 10.5V6.75a4.5 4.5 0 0 0-9 0v3.75m-.75 0h10.5A1.5 1.5 0 0 1 18.75 12v6.75a1.5 1.5 0 0 1-1.5 1.5H6.75a1.5 1.5 0 0 1-1.5-1.5V12a1.5 1.5 0 0 1 1.5-1.5Z"
                      />
                    </svg>

                    <span>
                      <strong className="font-black text-white/72">
                        Private
                      </strong>{" "}
                      unless you choose to share.
                    </span>
                  </div>

                  <ActionButton
                    type="submit"
                    disabled={!canSubmit}
                    fullWidth
                    icon="sprout"
                    size="sm"
                  >
                    Help Me Ask Gently
                  </ActionButton>

                  {wallContextLoading ? (
                    <p
                      role="status"
                      className="text-center text-sm font-semibold text-white/60"
                    >
                      Preparing this wall for your card...
                    </p>
                  ) : null}

                  {refineError ? (
                    <p className="text-center text-sm font-bold text-red-600">
                      {refineError}
                    </p>
                  ) : null}
                  </div>
                </form>
              )}

              <AccountLinkingSheet
                isOpen={isAccountLinkingOpen}
                intent="create-another"
                onDismiss={() =>
                  setIsAccountLinkingOpen(false)
                }
                onLinked={
                  continueAfterAccountLinking
                }
              />
            </>
          )}

        </section>
      </main>

      <Footer dark />
    </div>
  );
}
